(() => {
  if (window.ProhealthCartDelivery) return;

  const SELECTORS = {
    module: '[data-ph-delivery-module]',
    upsell: '[data-ph-cart-upsell]',
    list: '[data-ph-upsell-list]',
    card: '[data-ph-upsell-card]',
    next: '[data-ph-upsell-next]',
    thumb: '[data-ph-scroll-thumb]'
  };

  const state = {
    refreshTimer: null,
    cartSignature: sessionStorage.getItem('phCartSignature') || '',
    rotation: Number(sessionStorage.getItem('phUpsellRotation') || 0),
    requestId: 0
  };

  const uniqueCards = (cards, cartProducts) => {
    const seen = new Set();
    return cards.filter((card) => {
      const id = card.dataset.productId;
      if (!id || seen.has(id) || cartProducts.has(id)) return false;
      seen.add(id);
      return true;
    });
  };

  const parseCards = (html) => {
    const documentFragment = new DOMParser().parseFromString(html, 'text/html');
    return Array.from(documentFragment.querySelectorAll(SELECTORS.card));
  };

  const recommendationUrl = (productId, intent) => {
    const root = window.Shopify?.routes?.root || '/';
    const url = new URL(`${root}recommendations/products`, window.location.origin);
    url.searchParams.set('section_id', 'prohealth-cart-recommendations');
    url.searchParams.set('product_id', productId);
    url.searchParams.set('limit', '10');
    url.searchParams.set('intent', intent);
    return url.toString();
  };

  const getPool = async (module) => {
    const anchor = module.dataset.anchorProductId;
    if (!anchor) return [];

    const sourceMode = module.dataset.recommendationSource || 'automatic_and_selected';
    const intents = sourceMode === 'selected_only' ? ['related'] : ['complementary', 'related'];
    const responses = await Promise.all(
      intents.map((intent) =>
        fetch(recommendationUrl(anchor, intent), {
          headers: { Accept: 'text/html' }
        }).then((response) => response.ok ? response.text() : '')
      )
    );

    let cards = responses.flatMap(parseCards);

    if (sourceMode === 'automatic') {
      cards = cards.filter((card) => card.dataset.source === 'automatic' || card.dataset.source === 'fallback');
    } else if (sourceMode === 'selected_only') {
      cards = cards.filter((card) => card.dataset.source === 'selected' || card.dataset.source === 'fallback');
    }

    return cards;
  };

  const updateIndicator = (module) => {
    const list = module.querySelector(SELECTORS.list);
    const thumb = module.querySelector(SELECTORS.thumb);
    if (!list || !thumb) return;

    const maxScroll = Math.max(0, list.scrollWidth - list.clientWidth);
    const visibleRatio = list.scrollWidth ? Math.min(1, list.clientWidth / list.scrollWidth) : 1;
    const thumbWidth = Math.max(18, visibleRatio * 100);
    const travel = 100 - thumbWidth;
    const progress = maxScroll ? list.scrollLeft / maxScroll : 0;

    thumb.style.width = `${thumbWidth}%`;
    thumb.style.transform = `translateX(${progress * travel / (thumbWidth / 100)}%)`;
  };

  const bindModule = (module) => {
    const list = module.querySelector(SELECTORS.list);
    const next = module.querySelector(SELECTORS.next);
    if (!list || !next || list.dataset.phBound === 'true') return;

    list.dataset.phBound = 'true';
    list.addEventListener('scroll', () => updateIndicator(module), { passive: true });
    next.addEventListener('click', () => {
      const cards = list.querySelectorAll(SELECTORS.card);
      if (cards.length <= 1) return;

      const firstCard = cards[0];
      const step = firstCard.getBoundingClientRect().width + 10;
      const maxScroll = Math.max(0, list.scrollWidth - list.clientWidth);
      const isAtEnd = list.scrollLeft >= maxScroll - 4;
      const nextPosition = Math.min(list.scrollLeft + step, maxScroll);

      list.scrollTo({
        left: isAtEnd ? 0 : nextPosition,
        behavior: 'smooth'
      });
    });

    window.addEventListener('resize', () => updateIndicator(module), { passive: true });
  };

  const renderModule = async (module, requestId) => {
    const upsell = module.querySelector(SELECTORS.upsell);
    const list = module.querySelector(SELECTORS.list);
    if (!upsell || !list) return;

    upsell.classList.add('is-loading');
    bindModule(module);

    try {
      const cartProducts = new Set(
        (module.dataset.cartProducts || '').split('|').filter(Boolean)
      );
      const remaining = Number(module.dataset.remaining || 0);
      const pool = uniqueCards(await getPool(module), cartProducts);

      if (requestId !== state.requestId || !module.isConnected) return;

      pool.sort((a, b) => {
        const sourceWeight = { automatic: 0, selected: 1, fallback: 2 };
        const sourceDifference =
          (sourceWeight[a.dataset.source] ?? 3) -
          (sourceWeight[b.dataset.source] ?? 3);
        if (sourceDifference !== 0) return sourceDifference;

        const priceA = Number(a.dataset.price || 0);
        const priceB = Number(b.dataset.price || 0);
        return Math.abs(priceA - remaining) - Math.abs(priceB - remaining);
      });

      const configuredLimit = Number.parseInt(module.dataset.upsellLimit, 10);
      const productLimit = Number.isFinite(configuredLimit)
        ? Math.min(12, Math.max(1, configuredLimit))
        : 4;
      const required = Math.min(productLimit, pool.length);
      const offset = pool.length ? (state.rotation * productLimit) % pool.length : 0;
      const selected = [];

      for (let index = 0; index < pool.length && selected.length < required; index += 1) {
        selected.push(pool[(offset + index) % pool.length]);
      }

      list.replaceChildren(...selected);
      const next = module.querySelector(SELECTORS.next);
      if (next) next.hidden = selected.length <= 1;
      upsell.classList.toggle('is-empty', selected.length === 0);
      requestAnimationFrame(() => updateIndicator(module));
    } catch (error) {
      console.error('Prohealth cart recommendations:', error);
      upsell.classList.add('is-empty');
    } finally {
      upsell.classList.remove('is-loading');
    }
  };

  const refreshAll = async ({ rotate = false } = {}) => {
    window.clearTimeout(state.refreshTimer);
    state.refreshTimer = window.setTimeout(async () => {
      if (rotate) {
        state.rotation += 1;
        sessionStorage.setItem('phUpsellRotation', String(state.rotation));
      }

      state.requestId += 1;
      const requestId = state.requestId;
      document.querySelectorAll(SELECTORS.module).forEach((module) => {
        renderModule(module, requestId);
      });
    }, 80);
  };

  const checkCartChange = async () => {
    try {
      const root = window.Shopify?.routes?.root || '/';
      const cart = await fetch(`${root}cart.js`, {
        headers: { Accept: 'application/json' }
      }).then((response) => response.json());

      const signature = cart.items
        .map((item) => `${item.key}:${item.quantity}`)
        .join('|');

      const changed = Boolean(state.cartSignature && signature !== state.cartSignature);
      state.cartSignature = signature;
      sessionStorage.setItem('phCartSignature', signature);
      refreshAll({ rotate: changed });
    } catch (error) {
      refreshAll();
    }
  };

  const addProduct = async (button) => {
    if (button.classList.contains('is-loading')) return;

    const variantId = Number(button.dataset.variantId);
    if (!variantId) return;

    const originalText = button.innerHTML;
    button.classList.add('is-loading');
    button.setAttribute('aria-busy', 'true');

    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.description || 'Unable to add this product.');
      }

      state.rotation += 1;
      sessionStorage.setItem('phUpsellRotation', String(state.rotation));

      const drawer = document.querySelector('m-cart-drawer');
      const cartPage = document.querySelector('m-cart');

      if (drawer && typeof drawer.onCartDrawerUpdate === 'function') {
        drawer.onCartDrawerUpdate(true);
      }
      if (cartPage && typeof cartPage.onCartUpdate === 'function') {
        cartPage.onCartUpdate(true);
      }

      if (window.MinimogEvents && window.MinimogTheme?.pubSubEvents?.cartUpdate) {
        window.MinimogEvents.emit(window.MinimogTheme.pubSubEvents.cartUpdate, {
          source: 'prohealth-cart-upsell'
        });
      }

      window.setTimeout(checkCartChange, 450);
    } catch (error) {
      button.classList.remove('is-loading');
      button.removeAttribute('aria-busy');
      button.innerHTML = originalText;

      if (window.MinimogTheme?.Notification?.show) {
        window.MinimogTheme.Notification.show({
          target: button.closest(SELECTORS.card),
          type: 'warning',
          message: error.message
        });
      } else {
        console.error(error);
      }
    }
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-ph-upsell-add]');
    if (!button) return;
    event.preventDefault();
    addProduct(button);
  });

  const observer = new MutationObserver((mutations) => {
    const cartChanged = mutations.some((mutation) =>
      Array.from(mutation.addedNodes).some((node) =>
        node.nodeType === 1 &&
        (node.matches?.(SELECTORS.module) || node.querySelector?.(SELECTORS.module))
      )
    );
    if (cartChanged) checkCartChange();
  });

  document.addEventListener('DOMContentLoaded', () => {
    observer.observe(document.body, { childList: true, subtree: true });
    checkCartChange();
  });

  window.setTimeout(() => {
    if (window.MinimogEvents && window.MinimogTheme?.pubSubEvents?.cartUpdate) {
      window.MinimogEvents.subscribe(
        window.MinimogTheme.pubSubEvents.cartUpdate,
        () => window.setTimeout(checkCartChange, 250)
      );
    }
  }, 0);

  window.ProhealthCartDelivery = { refresh: checkCartChange };
})();
